import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import packageJson from "../package.json";

import App from "./App";
import "./index.css";
import "react-toastify/dist/ReactToastify.css";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { getCompatibilityState } from "./services/appVersionService";
import { errorLogService } from "./services/errorLogService";
import { isRecoverableChunkLoadError } from "./utils/chunkLoadError";
import {
  consumePendingRecoveryEvent,
  createDiagnosticId,
  persistPendingRecoveryEvent,
} from "./utils/diagnosticsContext";

import logger from "./utils/logger";

const PWA_UPDATE_CHECK_DELAY_MS = 10000;
let recoverableChunkReloadRequested = false;

const flushPendingRecoveryEvent = () => {
  const pendingRecoveryEvent = consumePendingRecoveryEvent();

  if (!pendingRecoveryEvent) {
    return;
  }

  void errorLogService.logError({
    message:
      "Приложение восстановилось после принудительной перезагрузки ресурсов",
    severity: "warn",
    context: {
      error_meta: {
        action: "recovery_reload_completed",
        code: pendingRecoveryEvent.reason,
      },
      app_context: {
        module: "index",
        recoverySource: pendingRecoveryEvent.source,
        previousPath: pendingRecoveryEvent.path,
      },
      diagnostic_ids: {
        recoveryId: pendingRecoveryEvent.recoveryId,
      },
      runtime_context: {
        previousRecordedAt: pendingRecoveryEvent.recordedAt,
      },
    },
  });
};

const attemptRecoverableChunkReload = (error, source) => {
  const normalizedError =
    typeof error === "string" ? { message: error } : error;
  const recoveryReason = `${source}_chunk_error`;

  if (!isRecoverableChunkLoadError(normalizedError)) {
    return false;
  }

  if (recoverableChunkReloadRequested) {
    serviceWorkerRegistration.dispatchUpdateRecovery({
      reason: recoveryReason,
    });
    return true;
  }

  recoverableChunkReloadRequested = true;
  const recoveryId = createDiagnosticId("recovery");

  persistPendingRecoveryEvent({
    recoveryId,
    reason: recoveryReason,
    source,
    path: window.location.pathname,
  });

  logger.warn("PWA: chunk load recovery triggered", {
    source,
    error: normalizedError,
  });
  const reloadResult = serviceWorkerRegistration.reloadAppForUpdate({
    reason: recoveryReason,
  });

  if (reloadResult.skipped) {
    serviceWorkerRegistration.dispatchUpdateRecovery({
      reason: recoveryReason,
    });
  }

  return true;
};

// В dev-режиме чистим старые SW/кэш, чтобы не мешали локальной разработке
if (
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined" &&
  "serviceWorker" in navigator
) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((reg) => {
      reg.unregister().catch(() => {});
    });
  });

  if ("caches" in window) {
    caches.keys().then((names) => {
      names.forEach((name) => caches.delete(name));
    });
  }
}

// Global error handler to suppress AbortError in development
// and log real errors to Supabase in production
window.addEventListener("error", (event) => {
  if (
    attemptRecoverableChunkReload(
      event.error || new Error(event.message),
      "window_error",
    )
  ) {
    event.preventDefault();
    return false;
  }

  // Suppress AbortError
  if (
    event.message?.includes("AbortError") ||
    event.message?.includes("signal is aborted")
  ) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Suppressed AbortError (harmless in dev):", event.message);
    }
    event.preventDefault();
    return false;
  }

  // Suppress opaque 3rd-party script errors (Safari/WebView often reports only "Script error.")
  const isOpaqueScriptError =
    event.message === "Script error." && (!event.filename || event.colno === 0);
  if (isOpaqueScriptError) {
    event.preventDefault();
    return false;
  }

  // Log other errors in production
  if (process.env.NODE_ENV === "production") {
    logger.critical(
      "Global Uncaught Error",
      event.error || new Error(event.message),
      {
        lineno: event.lineno,
        colno: event.colno,
        filename: event.filename,
      },
    );
  }
});

window.addEventListener("unhandledrejection", (event) => {
  if (attemptRecoverableChunkReload(event.reason, "promise_rejection")) {
    event.preventDefault();
    return false;
  }

  // Suppress AbortError
  if (
    event.reason?.name === "AbortError" ||
    event.reason?.message?.includes("aborted")
  ) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "Suppressed unhandled AbortError (harmless in dev):",
        event.reason,
      );
    }
    event.preventDefault();
    return false;
  }

  // Log other rejections in production
  if (process.env.NODE_ENV === "production") {
    logger.critical("Unhandled Promise Rejection", event.reason, {
      type: "unhandledrejection",
    });
  }
});

const root = createRoot(document.getElementById("root"));

root.render(
  <BrowserRouter future={{ v7_relativeSplatPath: true }}>
    <App />
  </BrowserRouter>,
);

flushPendingRecoveryEvent();

// Boot-лог: только локально (console). Запись каждого открытия в БД
// заспамливала app_errors сотнями app_boot/info в сутки и топила реальные ошибки.
if (process.env.NODE_ENV === "production") {
  void Promise.resolve().then(() =>
    import("./utils/logger").then(({ default: bootLogger }) =>
      bootLogger.debug("app_boot", {
        path: window.location.pathname,
        swController: navigator.serviceWorker?.controller ? "yes" : "no",
      }),
    ),
  );
}

// Listen for SW_UPDATED_RELOAD broadcast from a newly-activated service worker.
// Это kill-switch для клиентов, которые месяцами сидят на старых бандлах
// (v1.5.294/339/456 и т.п.) — после нового деплоя старая вкладка один раз
// мягко перезагружается на свежий index.html и подхватывает актуальный main.*.js.
// Защита от петли: метка в sessionStorage — перезагружаемся не чаще раза в минуту.
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (!event.data || event.data.type !== "SW_UPDATED_RELOAD") return;
    try {
      const now = Date.now();
      const last = parseInt(
        sessionStorage.getItem("mgfood_sw_reload_ts") || "0",
        10,
      );
      if (now - last < 60000) return; // защита от петли
      sessionStorage.setItem("mgfood_sw_reload_ts", String(now));
      // Мягкая перезагрузка — session не теряется (лежит в localStorage)
      window.location.reload();
    } catch (e) {
      // ignore
    }
  });
}

// Register service worker for PWA functionality
if (process.env.NODE_ENV === "production") {
  serviceWorkerRegistration.register({
    onUpdate: (registration) => {
      const waitingServiceWorker = registration.waiting;
      if (waitingServiceWorker) {
        serviceWorkerRegistration.dispatchUpdateAvailable({
          waitingWorker: waitingServiceWorker,
          reason: "waiting_worker",
        });
      }
    },
  });

  getCompatibilityState(packageJson.version)
    .then(async (compatibilityState) => {
      const requiresRefresh =
        !compatibilityState.isSupported || compatibilityState.forceRefresh;

      if (!requiresRefresh) {
        serviceWorkerRegistration.clearAppRefreshGuard();
        return;
      }

      const reason = compatibilityState.forceRefresh
        ? "force_refresh"
        : "unsupported_build";
      const existingGuard = serviceWorkerRegistration.getAppRefreshGuard();
      let registration = null;

      if ("serviceWorker" in navigator) {
        try {
          registration = await navigator.serviceWorker.getRegistration();
        } catch (error) {
          logger.warn(
            "PWA: failed to read registration for compatibility check",
            error,
          );
        }
      }

      serviceWorkerRegistration.dispatchUpdateAvailable({
        waitingWorker: registration?.waiting || null,
        compatibilityState,
        isForced: true,
        reason,
        showUpdating: !existingGuard,
      });

      await serviceWorkerRegistration.requestAppRefresh({
        registration,
        waitingWorker: registration?.waiting || null,
        reason,
      });
    })
    .catch((error) => {
      logger.warn("PWA: startup compatibility check failed", error);
    });

  if ("serviceWorker" in navigator) {
    const triggerServiceWorkerUpdate = () => {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update().catch((err) => {
          // Silently ignore service worker update errors - these can occur
          // when the service-worker.js file is temporarily unavailable or
          // when the browser blocks the request due to security policies
          const isNetworkError =
            err?.message?.includes("fetching the script") ||
            err?.message?.includes("Failed to update");

          if (!isNetworkError) {
            console.warn("SW: Update check failed:", err);
          }
        });
      });
    };

    // Периодическая проверка обновлений в production
    // Проверяем после первого экрана, чтобы не мешать холодному старту PWA
    setTimeout(triggerServiceWorkerUpdate, PWA_UPDATE_CHECK_DELAY_MS);

    // Периодически проверяем каждые 5 часов
    setInterval(
      () => {
        triggerServiceWorkerUpdate();
      },
      1000 * 60 * 60 * 5, // 5 часов
    );

    // Проверяем при возвращении на страницу
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        triggerServiceWorkerUpdate();
      }
    });
  }
}
